/**
 * Verification Session List Component
 *
 * Displays a list of design verification sessions with search and actions.
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  Divider,
  Skeleton,
  Alert,
  Button,
} from '@mui/material';
import {
  Search as SearchIcon,
  MoreVert as MoreIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  Download as ExportIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { VerificationSession } from '../../types/verification';

interface VerificationSessionListProps {
  sessions: VerificationSession[];
  selectedSessionId: string | null;
  isLoading: boolean;
  error: string | null;
  onSessionSelect: (session: VerificationSession) => void;
  onSessionDelete?: (sessionId: string) => void;
  onSessionExport?: (sessionId: string) => void;
  onRefresh?: () => void;
  onCreate?: () => void;
}

const VerificationSessionList: React.FC<VerificationSessionListProps> = ({
  sessions,
  selectedSessionId,
  isLoading,
  error,
  onSessionSelect,
  onSessionDelete,
  onSessionExport,
  onRefresh,
  onCreate,
}) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuSessionId, setMenuSessionId] = useState<string | null>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, sessionId: string) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setMenuSessionId(sessionId);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuSessionId(null);
  };

  const handleDelete = () => {
    if (menuSessionId && onSessionDelete) {
      onSessionDelete(menuSessionId);
    }
    handleMenuClose();
  };

  const handleExport = () => {
    if (menuSessionId && onSessionExport) {
      onSessionExport(menuSessionId);
    }
    handleMenuClose();
  };

  const filteredSessions = sessions.filter((session) => {
    const query = searchQuery.toLowerCase();
    return (
      session.title.toLowerCase().includes(query) ||
      (session.modelName && session.modelName.toLowerCase().includes(query))
    );
  });

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading && sessions.length === 0) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Box sx={{ mb: 2 }}>
            <Skeleton variant="rectangular" height={40} />
          </Box>
          {[1, 2, 3].map((i) => (
            <Box key={i} sx={{ mb: 1 }}>
              <Skeleton variant="rectangular" height={72} />
            </Box>
          ))}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card variant="outlined">
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">
            {t('verification.sessions.title')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {onRefresh && (
              <IconButton onClick={onRefresh} size="small">
                <RefreshIcon />
              </IconButton>
            )}
            {onCreate && (
              <IconButton onClick={onCreate} size="small" color="primary">
                <AddIcon />
              </IconButton>
            )}
          </Box>
        </Box>

        <TextField
          fullWidth
          size="small"
          placeholder={t('common.search')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!isLoading && filteredSessions.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">
              {sessions.length === 0
                ? t('verification.sessions.noSessions')
                : t('common.noResults')}
            </Typography>
            {sessions.length === 0 && onCreate && (
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={onCreate}
                sx={{ mt: 2 }}
              >
                {t('verification.sessions.create')}
              </Button>
            )}
          </Box>
        )}

        <List disablePadding>
          {filteredSessions.map((session, index) => (
            <React.Fragment key={session.id}>
              {index > 0 && <Divider />}
              <ListItem disablePadding>
                <ListItemButton
                  selected={session.id === selectedSessionId}
                  onClick={() => onSessionSelect(session)}
                >
                  <ListItemText
                    primary={
                      <Typography variant="subtitle2">
                        {session.title}
                      </Typography>
                    }
                    secondary={
                      <Box>
                        {session.modelName && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {t('verification.modelName')}: {session.modelName}
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(session.createdAt)}
                          {session.units.length > 0 && ` | ${session.units.length} ${t('verification.units')}`}
                        </Typography>
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={(e) => handleMenuOpen(e, session.id)}
                    >
                      <MoreIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItemButton>
              </ListItem>
            </React.Fragment>
          ))}
        </List>

        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={() => {
            const session = sessions.find((s) => s.id === menuSessionId);
            if (session) onSessionSelect(session);
            handleMenuClose();
          }}>
            <ViewIcon fontSize="small" sx={{ mr: 1 }} />
            {t('common.view')}
          </MenuItem>
          {onSessionExport && (
            <MenuItem onClick={handleExport}>
              <ExportIcon fontSize="small" sx={{ mr: 1 }} />
              {t('verification.export.excel')}
            </MenuItem>
          )}
          {onSessionDelete && (
            <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
              <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
              {t('common.delete')}
            </MenuItem>
          )}
        </Menu>
      </CardContent>
    </Card>
  );
};

export default VerificationSessionList;
